import { createSelector } from 'reselect';
import { toShortCustomFieldId } from '../../../common/utils.tsx';
import type { State } from '../../types.tsx';
import { getIsCompanyManaged, getSprintFieldId } from '../app/index.tsx';
import {
	canAssignIssues,
	canBulkEditIssues,
	canCreateIssues,
	canDeleteIssues,
	canEditIssue,
	canTransitionIssues,
	canViewBoard,
} from '../permissions/index.tsx';

export const getConfig = (state: State) => state.config;

export const isConfigLoaded = (state: State) => state.ui.isConfigLoaded;

export const isCriticalDataLoaded = (state: State) => state.ui.isCriticalDataLoaded;

export const getIsInlineCardCreateEnabled = (state: State) =>
	getConfig(state).isInlineCardCreateEnabled;

const canViewBoardAndCreateOrMutateIssues = (state: State) =>
	canViewBoard(state) &&
	(canCreateIssues(state) ||
		canEditIssue(state) ||
		canDeleteIssues(state) ||
		canAssignIssues(state) ||
		canTransitionIssues(state) ||
		canBulkEditIssues(state));

const canCreateOrMutateIssues = (state: State) =>
	canCreateIssues(state) || canEditIssue(state) || canDeleteIssues(state);

export const isUserAtLeastMemberOfProject = (state: State) =>
	// This is a proxy for the DW idea of Project 'Member' but it's still flawed and not compatible with company-managed permission model.
	// If a user can create, edit or delete issues in this project, they must be at least a Member,
	// and enjoy all the privileges of a Project Member (e.g. creating and ranking issues).

	getIsCompanyManaged(state)
		? canViewBoardAndCreateOrMutateIssues(state)
		: canCreateOrMutateIssues(state);

export const isInlineIssueCreateAvailableForCurrentUser = createSelector(
	[isUserAtLeastMemberOfProject, getIsCompanyManaged, canCreateIssues],
	(isMember, isCompanyManaged, canUserCreateIssues) =>
		isCompanyManaged ? canUserCreateIssues : isMember,
);

export const getAllFeatures = (state: State) => state.config.allFeatures;

export const getShowEpicAsPanel = (state: State) =>
	!getIsCompanyManaged(state) || state.config.showEpicAsPanel;

export const getEpicLinkField = (state: State) => state.config.epicLinkField;
export const getEpicColorFieldId = (state: State) => state.config.epicColorFieldId;

export const epicLinkShortCustomFieldIdSelector = createSelector(
	[getEpicLinkField],
	(epicLinkField) => (epicLinkField?.id ? toShortCustomFieldId(epicLinkField?.id) : undefined),
);

export const sprintShortCustomFieldIdSelector = createSelector(
	[getSprintFieldId],
	(sprintCustomFieldId) =>
		sprintCustomFieldId ? toShortCustomFieldId(sprintCustomFieldId) : undefined,
);

export const getCanInviteOthersToProject = (state: State) => state.config.canInviteOthersToProject;
